import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@nosible/shared/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import { History, CreditCard } from "lucide-react";

type SubscriptionHistoryDialogProps = {
  subscriptionId: string;
  subscriptionName?: string;
};

export function SubscriptionHistoryDialog({
  subscriptionId,
  subscriptionName,
}: SubscriptionHistoryDialogProps) {
  const [open, setOpen] = useState(false);

  const {
    data: planHistory,
    isLoading,
    error,
  } = trpc.subscription.getPlanHistory.useQuery(
    { subscriptionId },
    { enabled: open && !!subscriptionId }
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <History className="h-4 w-4 mr-2" />
          Plan History
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-violet-pulse" />
            Plan History
          </DialogTitle>
          <DialogDescription>
            History of plan changes{subscriptionName ? ` for ${subscriptionName}` : " for this subscription"}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-chromium-mist">Loading...</div>
        ) : error ? (
          <div className="py-8 text-center text-red-400">{error.message}</div>
        ) : !planHistory || planHistory.length === 0 ? (
          <div className="py-8 text-center text-chromium-mist">
            <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No plan history found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Changed At</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {planHistory.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium text-silica capitalize">
                    {entry.planName || "Unknown"}
                  </TableCell>
                  <TableCell className="text-chromium-mist">
                    {entry.changedAt
                      ? new Date(entry.changedAt).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell className="text-chromium-mist">
                    {entry.notes || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
